import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	ArrowUturnLeftIcon,
	CalendarDaysIcon,
	CheckCircleIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	PencilIcon,
	SunIcon,
	TrashIcon,
	UserPlusIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog, Modal, Spinner } from '../../components/ui';
import { timeOffApi, ApiError, PROVIDER_TIME_OFF_REASONS } from '../../api';
import staffApi from '../../api/staff';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { StaffMember } from '../../types/staff';
import type {
	ProviderTimeOffRequest,
	TimeOffBookingResolution,
	TimeOffBookingResolutionRow,
	TimeOffBookingResolveRequest,
	TimeOffRecord,
	TimeOffResolution,
	TimeOffSaveAffectedBooking,
	TimeOffType,
} from '../../types/timeOff';

// ─── Helpers ────────────────────────────────────────────────────

type Tab = 'requests' | 'all';

const formatCurrency = (amount: number | null | undefined): string =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0);

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const formatStamp = (iso: string | null | undefined): string => {
	if (!iso) return '';
	const parsed = new Date(iso);
	return Number.isNaN(parsed.getTime())
		? ''
		: parsed.toLocaleString('en-US', {
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
			});
};

const formatIsoDate = (iso: string): string => {
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const prettifyRole = (role: string | null) =>
	role ? role.charAt(0) + role.slice(1).toLowerCase().replace(/_/g, ' ') : '';

const STATUS_STYLES: Record<string, string> = {
	BLOCKED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
	APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
	PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
	DECLINED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
	COMPLETED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const statusStyle = (record: TimeOffRecord) =>
	STATUS_STYLES[record.past && record.active ? 'COMPLETED' : record.status] ??
	STATUS_STYLES.COMPLETED;

const staffName = (s: StaffMember) => `${s.firstName} ${s.lastName}`.trim();

// ─── Request review modal ───────────────────────────────────────

/** Half-hour slots the salon could plausibly book, 7:00 AM – 8:00 PM. */
const RESCHEDULE_SLOTS: string[] = (() => {
	const out: string[] = [];
	for (let h = 7; h <= 20; h++) {
		out.push(`${String(h).padStart(2, '0')}:00`);
		if (h < 20) out.push(`${String(h).padStart(2, '0')}:30`);
	}
	return out;
})();

const formatSlot = (hhmm: string): string => {
	const [h, m] = hhmm.split(':').map(Number);
	const hour12 = h % 12 === 0 ? 12 : h % 12;
	return `${hour12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const RESOLUTION_STYLES: Record<TimeOffBookingResolution, string> = {
	UNRESOLVED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
	RESCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
	REASSIGNED: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
	CANCELLED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
	REFUNDED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
	COMPLETED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

type RowAction = 'RESCHEDULE' | 'REASSIGN' | 'CANCEL' | null;

const inputClass =
	'w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea]';

/** One affected booking with its state and, while unresolved, the three ways to deal with it. */
const BookingRow = ({
	row,
	timeOffId,
	locked,
	onResolved,
}: {
	row: TimeOffBookingResolutionRow;
	timeOffId: number;
	/** True once the request is decided or while another call is running. */
	locked: boolean;
	onResolved: (resolution: TimeOffResolution, message: string) => void;
}) => {
	const [action, setAction] = useState<RowAction>(null);
	const [date, setDate] = useState('');
	const [time, setTime] = useState('');
	const [staffId, setStaffId] = useState<number | ''>('');
	const [reason, setReason] = useState('');
	const [refund, setRefund] = useState(true);
	const [confirmCancel, setConfirmCancel] = useState(false);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		setAction(null);
		setConfirmCancel(false);
	}, [row.resolution]);

	const run = async (body: TimeOffBookingResolveRequest, success: string) => {
		setBusy(true);
		try {
			const next = await timeOffApi.resolveBooking(timeOffId, row.appointmentId, body);
			onResolved(next, success);
			setAction(null);
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not update the booking');
		} finally {
			setBusy(false);
		}
	};

	const refundLeft =
		row.paidAmount != null ? Math.max(row.paidAmount - (row.refundedAmount ?? 0), 0) : 0;
	const disabled = locked || busy;
	const showActions = !locked && row.resolution === 'UNRESOLVED';
	const showRefund = !locked && row.resolution === 'CANCELLED' && row.refundable;

	return (
		<li className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 px-4 py-3">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-sm font-semibold text-gray-900 dark:text-white">
						{row.customerName}
						{row.amount != null && (
							<span className="ml-2 text-xs font-bold text-gray-600 dark:text-gray-300">
								{formatCurrency(row.amount)}
							</span>
						)}
					</p>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{row.whenLabel ?? 'No time'} · {row.serviceName ?? 'Service'}
						{row.locationName ? ` · ${row.locationName}` : ''}
					</p>
					{row.resolution === 'REASSIGNED' && row.assignedStaffNames.length > 0 && (
						<p className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">
							Now with {row.assignedStaffNames.join(', ')}
						</p>
					)}
					{row.resolution === 'REFUNDED' && row.refundedAmount != null && (
						<p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
							{formatCurrency(row.refundedAmount)} sent back to the customer
						</p>
					)}
					{row.refundError && (
						<p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
							Refund failed: {row.refundError}
						</p>
					)}
				</div>
				<span
					className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${RESOLUTION_STYLES[row.resolution]}`}
				>
					{row.resolutionLabel}
				</span>
			</div>

			{showActions && action === null && (
				<div className="mt-3 flex flex-wrap gap-2">
					<Button
						variant="secondary"
						size="sm"
						disabled={disabled}
						onClick={() => setAction('RESCHEDULE')}
					>
						<CalendarDaysIcon className="w-4 h-4" /> Reschedule
					</Button>
					<Button
						variant="secondary"
						size="sm"
						disabled={disabled}
						onClick={() => setAction('REASSIGN')}
					>
						<UserPlusIcon className="w-4 h-4" /> Reassign
					</Button>
					<Button
						variant="danger"
						size="sm"
						disabled={disabled}
						onClick={() => {
							setConfirmCancel(false);
							setRefund(true);
							setAction('CANCEL');
						}}
					>
						<XCircleIcon className="w-4 h-4" /> Cancel booking
					</Button>
				</div>
			)}

			{showActions && action === 'RESCHEDULE' && (
				<div className="mt-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-3 space-y-2">
					<p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
						New date and time — must fall outside the time off
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
						<input
							type="date"
							className={inputClass}
							value={date}
							min={new Date().toISOString().slice(0, 10)}
							onChange={e => setDate(e.target.value)}
						/>
						<select
							className={inputClass}
							value={time}
							onChange={e => setTime(e.target.value)}
						>
							<option value="">-- Time --</option>
							{RESCHEDULE_SLOTS.map(slot => (
								<option key={slot} value={slot}>
									{formatSlot(slot)}
								</option>
							))}
						</select>
					</div>
					<div className="flex justify-end gap-2">
						<Button
							variant="secondary"
							size="sm"
							disabled={busy}
							onClick={() => setAction(null)}
						>
							Back
						</Button>
						<Button
							variant="primary"
							size="sm"
							isLoading={busy}
							disabled={disabled || !date || !time}
							onClick={() =>
								run(
									{
										action: 'RESCHEDULE',
										appointmentDateTime: `${date}T${time}:00`,
									},
									`${row.customerName} moved to ${formatIsoDate(date)} ${formatSlot(time)}`
								)
							}
						>
							Move booking
						</Button>
					</div>
				</div>
			)}

			{showActions && action === 'REASSIGN' && (
				<div className="mt-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-3 space-y-2">
					<p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
						Who takes this booking? Only staff who work here, hold the skill and are
						free then are listed.
					</p>
					{row.candidates.length === 0 ? (
						<p className="text-sm text-gray-500 dark:text-gray-400 italic">
							No one else at this location can take it at that time. Reschedule or
							cancel instead.
						</p>
					) : (
						<select
							className={inputClass}
							value={staffId}
							onChange={e => setStaffId(e.target.value ? Number(e.target.value) : '')}
						>
							<option value="">-- Choose a staff member --</option>
							{row.candidates.map(c => (
								<option key={c.staffId} value={c.staffId}>
									{c.name}
									{c.role ? ` · ${prettifyRole(c.role)}` : ''}
								</option>
							))}
						</select>
					)}
					<div className="flex justify-end gap-2">
						<Button
							variant="secondary"
							size="sm"
							disabled={busy}
							onClick={() => setAction(null)}
						>
							Back
						</Button>
						<Button
							variant="primary"
							size="sm"
							isLoading={busy}
							disabled={disabled || staffId === ''}
							onClick={() => {
								if (staffId === '') return;
								const name =
									row.candidates.find(c => c.staffId === staffId)?.name ??
									'the new stylist';
								run(
									{ action: 'REASSIGN', staffId },
									`${row.customerName} reassigned to ${name}`
								);
							}}
						>
							Reassign
						</Button>
					</div>
				</div>
			)}

			{showActions && action === 'CANCEL' && (
				<div className="mt-3 rounded-lg bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 p-3 space-y-2">
					<input
						type="text"
						className={inputClass}
						value={reason}
						maxLength={500}
						placeholder="Reason (sent to the customer)"
						onChange={e => setReason(e.target.value)}
					/>
					{row.paidAmount != null && row.paidAmount > 0 && (
						<label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
							<input
								type="checkbox"
								className="mt-0.5"
								checked={refund && row.refundable}
								disabled={!row.refundable}
								onChange={e => setRefund(e.target.checked)}
							/>
							<span>
								{row.refundable
									? `Refund the ${formatCurrency(refundLeft)} deposit now`
									: `Deposit ${formatCurrency(row.paidAmount)} — ${row.notRefundableReason ?? 'cannot be refunded here'}`}
							</span>
						</label>
					)}
					{confirmCancel ? (
						<p className="text-xs font-semibold text-red-700 dark:text-red-300">
							This cannot be undone. The customer is emailed about the cancellation.
						</p>
					) : null}
					<div className="flex justify-end gap-2">
						<Button
							variant="secondary"
							size="sm"
							disabled={busy}
							onClick={() => setAction(null)}
						>
							Back
						</Button>
						<Button
							variant="danger"
							size="sm"
							isLoading={busy}
							disabled={disabled}
							onClick={() => {
								if (!confirmCancel) {
									setConfirmCancel(true);
									return;
								}
								run(
									{
										action: 'CANCEL',
										refund: refund && row.refundable,
										reason: reason.trim() || undefined,
									},
									`${row.customerName}'s booking cancelled`
								);
							}}
						>
							{confirmCancel ? 'Yes, cancel booking' : 'Cancel booking'}
						</Button>
					</div>
				</div>
			)}

			{showRefund && (
				<div className="mt-3 flex items-center justify-between gap-3">
					<p className="text-xs text-gray-600 dark:text-gray-300">
						{formatCurrency(refundLeft)} deposit still with the salon.
					</p>
					<Button
						variant="secondary"
						size="sm"
						isLoading={busy}
						disabled={disabled}
						onClick={() =>
							run(
								{ action: 'REFUND', reason: reason.trim() || undefined },
								`${formatCurrency(refundLeft)} refunded to ${row.customerName}`
							)
						}
					>
						<ArrowUturnLeftIcon className="w-4 h-4" /> Refund{' '}
						{formatCurrency(refundLeft)}
					</Button>
				</div>
			)}
		</li>
	);
};

const RequestModal = ({
	record,
	onClose,
	onUpdated,
}: {
	record: TimeOffRecord | null;
	onClose: () => void;
	/** The record changed — decided, or a booking inside it was dealt with. */
	onUpdated: (updated: TimeOffRecord) => void;
}) => {
	const navigate = useNavigate();
	const [showDecline, setShowDecline] = useState(false);
	const [note, setNote] = useState('');
	const [busy, setBusy] = useState<'approve' | 'decline' | null>(null);
	const [resolution, setResolution] = useState<TimeOffResolution | null>(null);
	const [loadingResolution, setLoadingResolution] = useState(false);

	const recordId = record?.id ?? null;
	const recordStatus = record?.status ?? null;

	const loadResolution = useCallback(async (id: number) => {
		setLoadingResolution(true);
		try {
			setResolution(await timeOffApi.resolution(id));
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not load the affected bookings');
		} finally {
			setLoadingResolution(false);
		}
	}, []);

	useEffect(() => {
		setShowDecline(false);
		setNote('');
		setBusy(null);
		setResolution(null);
		if (recordId != null && recordStatus === 'PENDING') {
			loadResolution(recordId);
		}
	}, [recordId, recordStatus, loadResolution]);

	if (!record) return null;

	const shortfalls = record.noticeDays.filter(d => d.shortNotice);
	const rows = resolution?.bookings ?? [];
	const unresolved = resolution?.unresolvedCount ?? record.unresolvedBookingCount ?? 0;
	const canApprove = resolution ? resolution.canApprove : record.canApprove;
	const totalRevenue = rows.reduce((sum, b) => sum + (b.amount ?? 0), 0);

	const refreshRecord = async () => {
		try {
			onUpdated(await timeOffApi.get(record.id));
		} catch {
			/* the list refreshes on its next load */
		}
	};

	const decide = async (approve: boolean) => {
		if (!approve && !note.trim()) {
			toast.error('Add a note telling the staff member why');
			return;
		}
		setBusy(approve ? 'approve' : 'decline');
		try {
			const updated = approve
				? await timeOffApi.approve(record.id, note.trim() || undefined)
				: await timeOffApi.decline(record.id, note.trim());
			onUpdated(updated);
			toast.success(
				approve
					? `Approved — ${record.staffName} is blocked ${record.dateLabel}`
					: `Declined — ${record.staffName} stays available`
			);
			onClose();
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not save the decision');
			// A 409 here means a booking was added or changed since the list was read.
			if (approve) loadResolution(record.id);
		} finally {
			setBusy(null);
		}
	};

	return (
		<Modal isOpen onClose={onClose} title="Time-Off Request" size="xl">
			<div className="space-y-4">
				<div className="flex items-start gap-3">
					<div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
						<SunIcon className="w-6 h-6" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-base font-bold text-gray-900 dark:text-white">
							{record.staffName}
							{record.staffRole && (
								<span className="ml-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
									{prettifyRole(record.staffRole)}
								</span>
							)}
						</p>
						<p className="text-sm text-gray-600 dark:text-gray-300">
							{record.dateLabel} · {plural(record.dayCount, 'day')} ·{' '}
							{record.typeLabel}
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
							{record.locationName}
							{record.submittedAt
								? ` · requested ${formatStamp(record.submittedAt)}`
								: ''}
						</p>
					</div>
					<span
						className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusStyle(record)}`}
					>
						{record.statusLabel}
					</span>
				</div>

				{record.staffNote && (
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border-l-3 border-[#667eea]">
						<p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
							Note from {record.staffName.split(' ')[0]}
						</p>
						<p className="text-sm text-gray-700 dark:text-gray-300 italic">
							{record.staffNote}
						</p>
					</div>
				)}

				{/* Why it needs approval */}
				<div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
					<p className="text-sm font-bold text-amber-900 dark:text-amber-100">
						Why this needs your approval
					</p>
					<ul className="mt-1 list-disc pl-5 text-[13px] text-amber-900 dark:text-amber-100 space-y-0.5">
						{record.escalationReasons.map(r => (
							<li key={r}>{r}</li>
						))}
					</ul>
					{shortfalls.length > 0 && (
						<ul className="mt-2 space-y-1">
							{shortfalls.map(d => (
								<li
									key={d.date}
									className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 dark:bg-gray-800/40 px-3 py-1.5 text-xs text-amber-900 dark:text-amber-100"
								>
									<span className="font-semibold">
										{d.dayName} {formatIsoDate(d.date)}
									</span>
									<span className="text-[10px] font-bold uppercase tracking-wide bg-amber-200/70 dark:bg-amber-800/60 px-2 py-0.5 rounded-full">
										{d.weekend ? 'Weekend' : 'Weekday'}
									</span>
									<span>
										needs {Math.round(d.requiredNoticeHours / 24)} days — given{' '}
										{d.providedNoticeHours < 24
											? plural(d.providedNoticeHours, 'hour')
											: plural(Math.floor(d.providedNoticeHours / 24), 'day')}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>

				{/* Affected bookings — pending: the resolution workbench */}
				{record.status === 'PENDING' && (
					<div>
						<div className="flex flex-wrap items-center justify-between gap-2 mb-2">
							<h3 className="text-sm font-semibold text-gray-900 dark:text-white">
								Affected bookings
								{rows.length > 0 && (
									<span className="ml-2 text-xs font-bold text-gray-600 dark:text-gray-300">
										{plural(rows.length, 'booking')} ·{' '}
										{formatCurrency(totalRevenue)}
									</span>
								)}
							</h3>
							{resolution &&
								rows.length > 0 &&
								(unresolved > 0 ? (
									<span className="text-xs font-bold text-red-600 dark:text-red-400">
										{plural(unresolved, 'booking')} still need
										{unresolved === 1 ? 's' : ''} a resolution ·{' '}
										{formatCurrency(resolution.unresolvedRevenue)}
									</span>
								) : (
									<span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
										<CheckCircleIcon className="w-4 h-4" /> Every booking
										resolved
									</span>
								))}
						</div>
						{loadingResolution && !resolution ? (
							<div className="flex items-center gap-2 py-3 text-sm text-gray-500 dark:text-gray-400">
								<Spinner size="sm" /> Checking the bookings…
							</div>
						) : rows.length === 0 ? (
							<p className="text-sm text-gray-400 dark:text-gray-500 italic">
								No bookings affected.
							</p>
						) : (
							<>
								<ul className="space-y-2">
									{rows.map(b => (
										<BookingRow
											key={b.appointmentId}
											row={b}
											timeOffId={record.id}
											locked={busy !== null}
											onResolved={(next, message) => {
												setResolution(next);
												toast.success(message);
												refreshRecord();
											}}
										/>
									))}
								</ul>
								<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
									Nothing happens to these appointments on its own. Each one has
									to be rescheduled, reassigned or cancelled before the time off
									can be approved — here, or in{' '}
									<button
										type="button"
										onClick={() => navigate('/providers/appointments')}
										className="font-semibold text-[#667eea] underline underline-offset-2 cursor-pointer"
									>
										Appointments
									</button>
									. Customers are notified of every change.
								</p>
							</>
						)}
					</div>
				)}

				{/* Decided: what was done to the bookings */}
				{record.status !== 'PENDING' && record.bookingResolutions.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
							How the bookings were handled
						</h3>
						<ul className="space-y-1.5">
							{record.bookingResolutions.map((entry, i) => (
								<li
									key={`${entry.appointmentId}-${i}`}
									className="flex items-start justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 rounded-lg"
								>
									<div className="min-w-0">
										<p className="text-sm font-semibold text-gray-900 dark:text-white">
											{entry.customerName ?? 'Customer'}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{entry.detail}
											{entry.actorName ? ` · ${entry.actorName}` : ''} ·{' '}
											{formatStamp(entry.at)}
										</p>
									</div>
									<span
										className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${RESOLUTION_STYLES[entry.resolution]}`}
									>
										{entry.resolution === 'REFUNDED'
											? 'Cancelled & refunded'
											: entry.resolution.charAt(0) +
												entry.resolution.slice(1).toLowerCase()}
									</span>
								</li>
							))}
						</ul>
					</div>
				)}

				{record.status === 'PENDING' && (
					<>
						{showDecline ? (
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
									Why are you declining? <span className="text-red-500">*</span>
								</label>
								<textarea
									value={note}
									onChange={e => setNote(e.target.value)}
									rows={3}
									maxLength={500}
									placeholder="This is sent to the staff member."
									className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea]"
								/>
							</div>
						) : (
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
									Note for the staff member (optional)
								</label>
								<input
									type="text"
									value={note}
									onChange={e => setNote(e.target.value)}
									maxLength={500}
									className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea]"
								/>
							</div>
						)}
						<div className="flex flex-wrap items-center justify-end gap-3 pt-2">
							{!showDecline && !canApprove && rows.length > 0 && (
								<span className="mr-auto inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
									<ExclamationTriangleIcon className="w-4 h-4" />
									Resolve every booking before approving
								</span>
							)}
							{showDecline ? (
								<>
									<Button
										variant="secondary"
										onClick={() => setShowDecline(false)}
										disabled={busy !== null}
									>
										Back
									</Button>
									<Button
										variant="danger"
										onClick={() => decide(false)}
										isLoading={busy === 'decline'}
										disabled={busy !== null || !note.trim()}
									>
										<XCircleIcon className="w-4 h-4" /> Decline Request
									</Button>
								</>
							) : (
								<>
									<Button
										variant="danger"
										onClick={() => setShowDecline(true)}
										disabled={busy !== null}
									>
										Decline
									</Button>
									<Button
										variant="success"
										onClick={() => decide(true)}
										isLoading={busy === 'approve'}
										disabled={busy !== null || !canApprove || loadingResolution}
									>
										<CheckCircleIcon className="w-4 h-4" /> Approve
									</Button>
								</>
							)}
						</div>
					</>
				)}

				{record.status !== 'PENDING' && record.decidedAt && (
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{record.statusLabel} by {record.decidedByName ?? 'provider'} on{' '}
						{formatStamp(record.decidedAt)}
						{record.decisionNote ? ` — “${record.decisionNote}”` : ''}
					</p>
				)}
			</div>
		</Modal>
	);
};

// ─── Add / edit modal ───────────────────────────────────────────

interface EditModalProps {
	isOpen: boolean;
	record: TimeOffRecord | null;
	staff: StaffMember[];
	locationId: number;
	onClose: () => void;
	onSaved: (
		record: TimeOffRecord,
		warning: string | null,
		affected: TimeOffSaveAffectedBooking[]
	) => void;
}

const EditModal = ({ isOpen, record, staff, locationId, onClose, onSaved }: EditModalProps) => {
	const [staffId, setStaffId] = useState<number | ''>('');
	const [scope, setScope] = useState<'all' | 'here'>('all');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [fullDay, setFullDay] = useState(true);
	const [startTime, setStartTime] = useState('09:00');
	const [endTime, setEndTime] = useState('12:00');
	const [type, setType] = useState<TimeOffType>('VACATION');
	const [notes, setNotes] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		setStaffId(record?.staffId ?? '');
		setScope(record?.locationId ? 'here' : 'all');
		setStartDate(record?.startDate ?? '');
		setEndDate(record?.endDate ?? record?.startDate ?? '');
		setFullDay(record?.fullDay ?? true);
		setStartTime(record?.startTime?.slice(0, 5) || '09:00');
		setEndTime(record?.endTime?.slice(0, 5) || '12:00');
		setType(record?.type ?? 'VACATION');
		setNotes(record?.notes ?? '');
	}, [isOpen, record]);

	const isValid =
		staffId !== '' &&
		Boolean(startDate) &&
		Boolean(endDate) &&
		endDate >= startDate &&
		(fullDay || (Boolean(startTime) && Boolean(endTime) && startTime !== endTime));

	const handleSubmit = async () => {
		// `isValid` already proves staffId is a number; TypeScript narrows through the alias.
		if (!isValid) return;
		const payload: ProviderTimeOffRequest = {
			staffId,
			locationId: scope === 'here' ? locationId : null,
			startDate,
			endDate,
			fullDay,
			startTime: fullDay ? undefined : startTime,
			endTime: fullDay ? undefined : endTime,
			type,
			notes: notes.trim() || undefined,
		};
		setIsSaving(true);
		try {
			const result = record
				? await timeOffApi.update(record.id, payload)
				: await timeOffApi.create(payload);
			onSaved(result.timeOff, result.warning, result.affectedBookings ?? []);
			onClose();
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not save time off');
		} finally {
			setIsSaving(false);
		}
	};

	const inputClass =
		'w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={record ? 'Edit Time Off' : 'Add Time Off'}
			size="lg"
		>
			<div className="space-y-4">
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Staff member <span className="text-red-500">*</span>
						</label>
						<select
							value={staffId}
							onChange={e =>
								setStaffId(e.target.value === '' ? '' : Number(e.target.value))
							}
							className={inputClass}
						>
							<option value="">Select…</option>
							{staff.map(s => (
								<option key={s.id} value={s.id}>
									{staffName(s)}
								</option>
							))}
						</select>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Away from
						</label>
						<select
							value={scope}
							onChange={e => setScope(e.target.value as 'all' | 'here')}
							className={inputClass}
						>
							<option value="all">All locations</option>
							<option value="here">This location only</option>
						</select>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Start date <span className="text-red-500">*</span>
						</label>
						<input
							type="date"
							value={startDate}
							onChange={e => {
								setStartDate(e.target.value);
								if (!endDate || endDate < e.target.value)
									setEndDate(e.target.value);
							}}
							className={inputClass}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							End date <span className="text-red-500">*</span>
						</label>
						<input
							type="date"
							min={startDate}
							value={endDate}
							onChange={e => setEndDate(e.target.value)}
							className={inputClass}
						/>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-4">
					<label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
						<input
							type="radio"
							checked={fullDay}
							onChange={() => setFullDay(true)}
							className="accent-[#667eea]"
						/>
						Full day
					</label>
					<label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
						<input
							type="radio"
							checked={!fullDay}
							onChange={() => setFullDay(false)}
							className="accent-[#667eea]"
						/>
						Part of the day
					</label>
					{!fullDay && (
						<div className="flex items-center gap-2">
							<input
								type="time"
								value={startTime}
								onChange={e => setStartTime(e.target.value)}
								className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
							/>
							<span className="text-gray-400">–</span>
							<input
								type="time"
								value={endTime}
								onChange={e => setEndTime(e.target.value)}
								className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
							/>
						</div>
					)}
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Reason
						</label>
						<select
							value={type}
							onChange={e => setType(e.target.value as TimeOffType)}
							className={inputClass}
						>
							{PROVIDER_TIME_OFF_REASONS.map(reason => (
								<option key={reason.value} value={reason.value}>
									{reason.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Notes
						</label>
						<input
							type="text"
							value={notes}
							onChange={e => setNotes(e.target.value)}
							maxLength={500}
							placeholder="Anything the team should know"
							className={inputClass}
						/>
					</div>
				</div>

				<p className="text-xs text-gray-500 dark:text-gray-400">
					Takes effect as soon as it is saved: customers can’t book this staff member on
					these dates. Existing bookings are never cancelled — any that clash come back as
					a warning.
				</p>
			</div>

			<div className="flex justify-end gap-3 mt-6">
				<Button variant="secondary" onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={!isValid}
					isLoading={isSaving}
				>
					{record ? 'Save Changes' : 'Save Time Off'}
				</Button>
			</div>
		</Modal>
	);
};

// ─── Page ───────────────────────────────────────────────────────

const TimeOff = () => {
	const { selectedLocationId } = useProviderProfileStore();
	const [tab, setTab] = useState<Tab>('requests');
	const [records, setRecords] = useState<TimeOffRecord[]>([]);
	const [staff, setStaff] = useState<StaffMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [staffFilter, setStaffFilter] = useState<number | ''>('');
	const [upcomingOnly, setUpcomingOnly] = useState(true);
	const [reviewing, setReviewing] = useState<TimeOffRecord | null>(null);
	const [editing, setEditing] = useState<{ open: boolean; record: TimeOffRecord | null }>({
		open: false,
		record: null,
	});
	const [removing, setRemoving] = useState<TimeOffRecord | null>(null);
	const [isRemoving, setIsRemoving] = useState(false);
	const [saveWarning, setSaveWarning] = useState<{
		warning: string;
		affected: TimeOffSaveAffectedBooking[];
	} | null>(null);

	const load = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		setError(null);
		try {
			const [list, team] = await Promise.all([
				timeOffApi.list({ locationId: selectedLocationId, upcomingOnly, status: 'ALL' }),
				staffApi.listStaff(selectedLocationId),
			]);
			setRecords(list);
			setStaff(team);
		} catch (err) {
			setError((err as ApiError).message || 'Failed to load time off');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, upcomingOnly]);

	useEffect(() => {
		load();
	}, [load]);

	const pending = useMemo(
		() =>
			records
				.filter(r => r.status === 'PENDING')
				.sort((a, b) => (a.submittedAt ?? '').localeCompare(b.submittedAt ?? '')),
		[records]
	);

	const visible = useMemo(
		() =>
			records
				.filter(r => r.status !== 'WITHDRAWN' && r.status !== 'REMOVED')
				.filter(r => staffFilter === '' || r.staffId === staffFilter)
				.sort((a, b) =>
					a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0
				),
		[records, staffFilter]
	);

	const upsert = (record: TimeOffRecord) =>
		setRecords(prev => [...prev.filter(r => r.id !== record.id), record]);

	const handleRemove = async () => {
		if (!removing) return;
		setIsRemoving(true);
		try {
			await timeOffApi.remove(removing.id);
			setRecords(prev => prev.filter(r => r.id !== removing.id));
			toast.success(`Time off removed — ${removing.staffName} is bookable again`);
			setRemoving(null);
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not remove time off');
		} finally {
			setIsRemoving(false);
		}
	};

	if (!selectedLocationId && !isLoading) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-2">
					<ClockIcon className="w-12 h-12 text-gray-300 dark:text-gray-600" />
					<p className="text-gray-500 dark:text-gray-400">
						Select a location to manage time off.
					</p>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-wrap items-center justify-between gap-4 mb-6">
				<div className="flex items-center gap-4">
					<div
						className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
						style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
					>
						<SunIcon className="w-6 h-6 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
							Time Off
						</h1>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
							Staff requests waiting on you, and every absence on the books
						</p>
					</div>
				</div>
				<Button
					variant="primary"
					size="sm"
					onClick={() => setEditing({ open: true, record: null })}
				>
					+ Add Time Off
				</Button>
			</div>

			{saveWarning && (
				<div className="rounded-xl border-2 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 mb-5">
					<div className="flex items-start gap-3">
						<ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0 text-amber-600 dark:text-amber-400" />
						<div className="flex-1 min-w-0">
							<p className="text-[13px] text-amber-900 dark:text-amber-200">
								{saveWarning.warning}
							</p>
							<ul className="mt-2 text-xs text-amber-900 dark:text-amber-200 space-y-0.5">
								{saveWarning.affected.map(b => (
									<li key={b.appointmentId}>
										{b.whenLabel ?? b.appointmentDateTime} ·{' '}
										{b.serviceName ?? 'Appointment'}
										{b.locationName ? ` · ${b.locationName}` : ''}
									</li>
								))}
							</ul>
						</div>
						<Button variant="secondary" size="sm" onClick={() => setSaveWarning(null)}>
							Dismiss
						</Button>
					</div>
				</div>
			)}

			{/* Tabs */}
			<div className="flex gap-2 mb-5">
				{(
					[
						['requests', `Requests${pending.length ? ` (${pending.length})` : ''}`],
						['all', 'All Time Off'],
					] as [Tab, string][]
				).map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => setTab(key)}
						className={`px-4 py-2 rounded-full text-sm font-semibold border cursor-pointer transition-colors ${
							tab === key
								? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
								: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
						}`}
					>
						{label}
					</button>
				))}
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			) : error ? (
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<Button variant="primary" size="sm" onClick={load}>
						Retry
					</Button>
				</div>
			) : tab === 'requests' ? (
				<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700">
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
						Waiting on you
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
						Staff can block their own dates with enough notice. These needed more —
						short notice, or a booking in the way. Their calendar stays open until you
						decide.
					</p>
					{pending.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-10 text-center">
							<CheckCircleIcon className="w-10 h-10 text-emerald-300 dark:text-emerald-700 mb-2" />
							<p className="text-sm text-gray-500 dark:text-gray-400">
								No requests waiting. Everything on the books is already blocked.
							</p>
						</div>
					) : (
						<ul className="space-y-2">
							{pending.map(r => (
								<li
									key={r.id}
									className="flex items-start gap-3 rounded-xl border-l-4 border-l-amber-500 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3.5"
								>
									<div className="min-w-0 flex-1">
										<p className="text-sm font-bold text-gray-900 dark:text-white">
											{r.staffName}
											{r.staffRole && (
												<span className="ml-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
													{prettifyRole(r.staffRole)}
												</span>
											)}
										</p>
										<p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
											{r.dateLabel} · {plural(r.dayCount, 'day')} ·{' '}
											{r.typeLabel} · {r.locationName}
										</p>
										<p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
											{r.escalationReasons.join('; ')}
										</p>
										{(r.affectedBookings?.length ?? 0) > 0 ? (
											<p className="text-xs font-semibold text-red-600 dark:text-red-400 mt-1">
												{plural(r.affectedBookings!.length, 'booking')} to
												resolve ·{' '}
												{formatCurrency(
													r.affectedBookings!.reduce(
														(s, b) => s + (b.amount ?? 0),
														0
													)
												)}
											</p>
										) : (
											(r.affectedBookingCount ?? 0) > 0 && (
												<p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
													Bookings resolved — ready to approve
												</p>
											)
										)}
									</div>
									<Button
										variant="primary"
										size="sm"
										onClick={() => setReviewing(r)}
									>
										Review
									</Button>
								</li>
							))}
						</ul>
					)}
				</div>
			) : (
				<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700">
					<div className="flex flex-wrap items-center gap-3 mb-4">
						<select
							value={staffFilter}
							onChange={e =>
								setStaffFilter(e.target.value === '' ? '' : Number(e.target.value))
							}
							className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
						>
							<option value="">All staff</option>
							{staff.map(s => (
								<option key={s.id} value={s.id}>
									{staffName(s)}
								</option>
							))}
						</select>
						<label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
							<input
								type="checkbox"
								checked={upcomingOnly}
								onChange={e => setUpcomingOnly(e.target.checked)}
								className="accent-[#667eea]"
							/>
							Upcoming only
						</label>
					</div>

					{visible.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-10 text-center">
							<SunIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
							<p className="text-sm text-gray-500 dark:text-gray-400">
								No time off on the books. Record a vacation or absence and that
								staff member stops being counted as available for those dates.
							</p>
						</div>
					) : (
						<ul className="space-y-2">
							{visible.map(r => (
								<li
									key={r.id}
									className={`flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3.5 ${
										r.past ? 'opacity-70' : ''
									}`}
								>
									<div className="min-w-0 flex-1">
										<p className="text-sm font-bold text-gray-900 dark:text-white">
											{r.staffName}
											<span className="ml-2 text-xs font-medium text-gray-500 dark:text-gray-400">
												{r.typeLabel}
											</span>
										</p>
										<p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
											{r.dateLabel} ·{' '}
											{r.fullDay ? plural(r.dayCount, 'day') : r.timeLabel} ·{' '}
											{r.locationName}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
											{r.requestedByStaff
												? 'Entered by staff'
												: 'Entered by you'}
											{r.notes ? ` · ${r.notes}` : ''}
											{r.status === 'DECLINED' && r.decisionNote
												? ` · Declined: ${r.decisionNote}`
												: ''}
										</p>
									</div>
									<div className="flex items-center gap-2 flex-shrink-0">
										<span
											className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusStyle(r)}`}
										>
											{r.statusLabel}
										</span>
										{r.status === 'PENDING' ? (
											<Button
												variant="secondary"
												size="sm"
												onClick={() => setReviewing(r)}
											>
												Review
											</Button>
										) : (
											r.active &&
											!r.past && (
												<>
													<button
														type="button"
														onClick={() =>
															setEditing({ open: true, record: r })
														}
														title="Edit"
														aria-label="Edit time off"
														className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-pointer"
													>
														<PencilIcon className="w-4 h-4" />
													</button>
													<button
														type="button"
														onClick={() => setRemoving(r)}
														title="Remove"
														aria-label="Remove time off"
														className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer"
													>
														<TrashIcon className="w-4 h-4" />
													</button>
												</>
											)
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			<RequestModal
				record={reviewing}
				onClose={() => setReviewing(null)}
				onUpdated={updated => {
					upsert(updated);
					setReviewing(current => (current?.id === updated.id ? updated : current));
				}}
			/>

			{selectedLocationId && (
				<EditModal
					isOpen={editing.open}
					record={editing.record}
					staff={staff}
					locationId={selectedLocationId}
					onClose={() => setEditing({ open: false, record: null })}
					onSaved={(record, warning, affected) => {
						upsert(record);
						toast.success(editing.record ? 'Time off updated' : 'Time off saved');
						setSaveWarning(warning ? { warning, affected } : null);
					}}
				/>
			)}

			<ConfirmDialog
				isOpen={removing !== null}
				onClose={() => (isRemoving ? undefined : setRemoving(null))}
				onConfirm={handleRemove}
				isLoading={isRemoving}
				title="Remove this time off?"
				confirmLabel="Remove Time Off"
				message={
					removing
						? `${removing.staffName} will be bookable again for ${removing.dateLabel}. Existing bookings are not changed.`
						: ''
				}
			/>
		</AppLayout>
	);
};

export default TimeOff;
